/* eslint-disable jsx-a11y/anchor-is-valid */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Module } from '../memed/domain/Module'
import { AtendimentoView } from '../../atendimento/core/_models'
import { FuncionarioMemed } from '../../funcionarios/funcionarios-list/core/_models'
import { formataDataIso } from '../../agendamentos/core/agendaHelpers'
import { adicionaAlergia } from '../../paciente-management/pacientes-list/core/_requests'
import {
  adicionaPrescricao,
  adicionaProcedimento,
  getAtendimentoById,
} from '../../atendimento/core/_requests'
import { Alergia, Medicacao, Prescricao, Procedimento, Retorno } from '../memed/_models'
import { getMemedFuncionario } from '../../funcionarios/funcionarios-list/core/_requests'
import { getErrorMessage, showError } from '../../../../../_metronic/helpers'
import { Button, InfoAlert } from '../../../../components'

const MEMED_URL =
  window.location.hostname.includes('homolog') || window.location.hostname.includes('localhost')
    ? 'https://integrations.memed.com.br/modulos/plataforma.sinapse-prescricao/build/sinapse-prescricao.min.js'
    : 'https://partners.memed.com.br/integration.js'

/** Tempo máximo de espera pelo módulo da Memed antes de assumir que ele não vai abrir. */
const TEMPO_LIMITE_MODULO = 30000

type FalhaCarregamento = {
  titulo: string
  mensagem: string
  detalhe?: string
}

type DadosPrescricao = {
  alergias: Alergia[]
  procedimentos: Procedimento[]
  prescricao?: Prescricao
}

function removerCaracteresEspeciais(string: string) {
  return string.replace(/([.-])/g, '')
}
function removerEspacosBrancos(string: string) {
  return string.replace(/\s/g, '')
}

/** Extrai a mensagem do backend (`{data: {message}}`) sem quebrar quando o corpo vem em outro formato. */
function extraiDetalheErro(erro: unknown): string | undefined {
  const mensagem = getErrorMessage(erro)
  return typeof mensagem === 'string' && mensagem.trim() !== '' ? mensagem : undefined
}

/**
 * O backend devolve a situação do cadastro do prescritor na Memed. Sem esta validação um cadastro
 * inativo recebia um token que a Memed recusa, o módulo não abria e o médico ficava na tela branca.
 */
function validaCadastroMemed(medico?: FuncionarioMemed): FalhaCarregamento | undefined {
  if (!medico) {
    return {
      titulo: 'Cadastro na Memed não encontrado',
      mensagem:
        'Não localizamos o seu cadastro de prescritor na Memed. Procure o administrador da clínica para regularizar o acesso.',
    }
  }
  if (medico.permitePrescrever === false) {
    return {
      titulo: 'Cadastro na Memed indisponível',
      mensagem:
        'Seu cadastro de prescritor na Memed não está liberado para prescrever. Regularize o cadastro junto à Memed ou ao administrador da clínica e tente novamente.',
      detalhe: medico.status ? `Situação do cadastro: ${medico.status}.` : undefined,
    }
  }
  if (!medico.token) {
    return {
      titulo: 'Cadastro na Memed incompleto',
      mensagem:
        'Não recebemos o token de acesso do seu cadastro na Memed, então o módulo de prescrição não pode ser aberto.',
    }
  }
  return undefined
}

export const MemedReceita = () => {
  const { atendimentoId } = useParams<{ atendimentoId: string }>()

  const [loading, setLoading] = useState(true)
  const [falha, setFalha] = useState<FalhaCarregamento | undefined>()
  const [medico, setMedico] = useState<FuncionarioMemed | undefined>()
  const [selectedAtendimento, setSelectedAtendimento] = useState<AtendimentoView | undefined>()
  const [tentativa, setTentativa] = useState(0)

  const scriptRef = useRef<HTMLScriptElement | null>(null)

  const registraFalha = useCallback((novaFalha: FalhaCarregamento, erro?: any) => {
    if (erro !== undefined) {
      console.error(`[Memed] ${novaFalha.titulo}`, erro)
    }
    setFalha(novaFalha)
    setLoading(false)
  }, [])

  const tentarNovamente = () => {
    // O SDK da Memed não pode ser reinicializado sobre uma carga anterior: se o script já foi
    // injetado, recarregar a página é a forma segura de tentar de novo.
    if (scriptRef.current) {
      window.location.reload()
      return
    }
    setTentativa((anterior) => anterior + 1)
  }

  useEffect(() => {
    let ativo = true

    const carregarDados = async () => {
      setLoading(true)
      setFalha(undefined)

      if (!atendimentoId) {
        registraFalha({
          titulo: 'Atendimento não informado',
          mensagem:
            'Não foi possível identificar o atendimento desta prescrição. Feche esta aba e abra a prescrição novamente pelo atendimento.',
        })
        return
      }

      let atendimento: AtendimentoView | undefined
      try {
        atendimento = await getAtendimentoById(Number(atendimentoId))
      } catch (erro) {
        if (!ativo) return
        registraFalha(
          {
            titulo: 'Não foi possível carregar o atendimento',
            mensagem:
              'Não conseguimos buscar os dados do atendimento necessários para abrir a prescrição.',
            detalhe: extraiDetalheErro(erro),
          },
          erro
        )
        return
      }

      let funcionario: FuncionarioMemed | undefined
      try {
        funcionario = await getMemedFuncionario(Number(atendimentoId))
      } catch (erro) {
        if (!ativo) return
        registraFalha(
          {
            titulo: 'Não é possível prescrever pela Memed',
            mensagem: 'Não conseguimos validar o seu cadastro de prescritor na Memed.',
            detalhe: extraiDetalheErro(erro),
          },
          erro
        )
        return
      }

      if (!ativo) return

      const impedimento = validaCadastroMemed(funcionario)
      if (impedimento) {
        registraFalha(impedimento)
        return
      }

      setSelectedAtendimento(atendimento)
      setMedico(funcionario)
    }

    carregarDados()

    return () => {
      ativo = false
    }
  }, [atendimentoId, tentativa, registraFalha])

  useEffect(() => {
    if (!medico?.token || !selectedAtendimento) {
      return
    }

    let moduloIniciado = false
    let timeoutModulo: ReturnType<typeof setTimeout> | undefined

    const falhaModulo = (falhaCarregamento: FalhaCarregamento, erro?: any) => {
      if (moduloIniciado) return
      moduloIniciado = true
      registraFalha(falhaCarregamento, erro)
    }

    const trataDadosRetorno = (retorno: Retorno): DadosPrescricao => {
      const alergias: Alergia[] = []
      const procedimentos: Procedimento[] = []
      const medicacoes: Medicacao[] = []

      const alergiasRetorno = retorno.prescricao?.paciente?._relationships?.allergy ?? []
      alergiasRetorno.forEach((alergia) => {
        alergias.push({ descricao: String(alergia.ingredient_name) })
      })

      const medicamentosRetorno = retorno.prescricao?.medicamentos ?? []
      medicamentosRetorno.forEach((m) => {
        if (m.tipo === 'exame') {
          const exame: Procedimento = {
            nome: String(m.nome),
            descricao: m.descricao === '' ? String(m.sanitized_posology) : String(m.descricao),
          }
          procedimentos.push(exame)
        } else {
          const medicamento: Medicacao = {
            identificador: String(m.real_id),
            nome: String(m.nome),
            descricao: String(m.descricao),
            titulo: String(m.titulo),
            subTitulo: String(m.subtitulo),
            posologia: String(m.sanitized_posology),
            composicao: String(m.composicao),
            apresentacao: '',
            quantidade: Number(m.quantidade),
            fabricante: String(m.fabricante),
            titularidade: String(m.titularidade),
            receituario: String(m.receituario),
            tipo: String(m.tipo),
            tarja: String(m.tarja),
            unit: String(m.form_units[0].singular),
            cid: String(m.cid),
            via: String(m.via),
            controleEspecial: Boolean(m.controle_especial),
            altoCusto: Boolean(m.alto_custo),
            descontinuado: Boolean(m.descontinuado),
            tratamentoPrevio: Boolean(m.tratamento_previo),
            descricaoTratamento: String(m.descricao_tratamento),
          }
          medicacoes.push(medicamento)
        }
      })

      const prescricao: Prescricao = {
        data:
          retorno.prescricao.prescriptionDateOriginal !== undefined
            ? retorno.prescricao.prescriptionDateOriginal
            : '2023-01-01 00:00:00',
        medicamentos: medicacoes,
        identificadoMedico: String(medico?.idExterno),
        identificadorPrescricao: String(retorno.prescricao.id),
      }

      return { alergias, procedimentos, prescricao }
    }

    const enviarDados = ({ alergias, procedimentos, prescricao }: DadosPrescricao) => {
      alergias.forEach((a) => {
        adicionaAlergia(selectedAtendimento?.paciente?.id, a.descricao).catch(showError)
      })

      if (prescricao === undefined) {
        return
      }

      const dt = new Date()
      adicionaPrescricao(selectedAtendimento?.id, prescricao)
        .then(() => {
          procedimentos.forEach((p) => {
            adicionaProcedimento(
              selectedAtendimento?.id,
              formataDataIso(dt),
              p.nome,
              p.descricao
            ).catch(showError)
          })
        })
        .catch(showError)
    }

    const iniciaModulo = () => {
      try {
        if (!window.MdSinapsePrescricao?.event?.add) {
          falhaModulo({
            titulo: 'Não foi possível abrir a prescrição',
            mensagem:
              'O módulo da Memed foi carregado, mas não respondeu como esperado. Isso costuma acontecer quando o cadastro do prescritor está inativo ou o acesso foi recusado.',
          })
          return
        }

        window.MdSinapsePrescricao.event.add('core:moduleInit', async function (module: Module) {
          if (module.name !== 'plataforma.prescricao') {
            return
          }
          try {
            moduloIniciado = true
            if (timeoutModulo) clearTimeout(timeoutModulo)
            setLoading(false)
            await window.MdHub.command.send('plataforma.prescricao', 'setFeatureToggle', {
              buttonClose: false,
            })
            window.MdHub.event.add('prescricaoImpressa', function (prescriptionData: any) {
              try {
                enviarDados(trataDadosRetorno(prescriptionData))
              } catch (erro) {
                console.error('[Memed] Falha ao registrar a prescrição impressa', erro)
                showError(erro)
              }
            })
            await window.MdHub.command.send('plataforma.prescricao', 'setPaciente', {
              idExterno: selectedAtendimento?.paciente?.id,
              nome: selectedAtendimento?.paciente?.nome,
              telefone:
                selectedAtendimento?.paciente?.pessoa?.telefone !== null
                  ? removerEspacosBrancos(
                      removerCaracteresEspeciais(
                        String(selectedAtendimento?.paciente?.pessoa?.telefone)
                      )
                    )
                  : '99999999999',
              cpf: String(selectedAtendimento?.paciente?.pessoa?.cpf),
            })
            window.MdHub.module.show('plataforma.prescricao')
          } catch (erro) {
            moduloIniciado = false
            falhaModulo(
              {
                titulo: 'Não foi possível abrir a prescrição',
                mensagem:
                  'O módulo da Memed carregou, mas não conseguimos preparar a prescrição deste atendimento.',
                detalhe: extraiDetalheErro(erro),
              },
              erro
            )
          }
        })
      } catch (erro) {
        falhaModulo(
          {
            titulo: 'Não foi possível abrir a prescrição',
            mensagem: 'Houve um erro inesperado ao iniciar o módulo de prescrição da Memed.',
            detalhe: extraiDetalheErro(erro),
          },
          erro
        )
      }
    }

    const script = document.createElement('script')
    script.src = MEMED_URL
    script.dataset.token = medico.token
    script.dataset.container = 'memed_div'
    script.addEventListener('load', iniciaModulo)
    script.addEventListener('error', (erro) =>
      falhaModulo(
        {
          titulo: 'Não foi possível carregar o módulo de prescrição',
          mensagem:
            'O serviço da Memed não respondeu. Verifique a sua conexão e tente novamente em instantes.',
        },
        erro
      )
    )
    document.body.appendChild(script)
    scriptRef.current = script

    timeoutModulo = setTimeout(
      () =>
        falhaModulo({
          titulo: 'O módulo de prescrição demorou demais para abrir',
          mensagem:
            'A Memed não concluiu a abertura da prescrição. Tente novamente ou utilize a prescrição Lauduz enquanto isso.',
        }),
      TEMPO_LIMITE_MODULO
    )

    return () => {
      if (timeoutModulo) clearTimeout(timeoutModulo)
      script.remove()
      scriptRef.current = null
    }
  }, [medico, selectedAtendimento, registraFalha])

  return (
    <div id='memed_div' className='h-100'>
      {falha && (
        <div className='d-flex flex-center flex-column flex-row-fluid pt-12 px-5'>
          <InfoAlert variant='danger' title={falha.titulo} className='mw-600px w-100'>
            <p className='mb-1'>{falha.mensagem}</p>
            {falha.detalhe && <p className='mb-0 fs-7 text-muted'>{falha.detalhe}</p>}
          </InfoAlert>
          <div className='d-flex gap-3'>
            <Button className='btn-primary' onClick={tentarNovamente}>
              Tentar novamente
            </Button>
            <Button className='btn-secondary' onClick={() => window.close()}>
              Fechar
            </Button>
          </div>
        </div>
      )}
      {loading && !falha && (
        <div className='d-flex flex-center flex-row-fluid pt-12'>
          <span className='indicator-progress fw-bold' style={{ display: 'block' }}>
            O módulo de prescrição está sendo carregado...
            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
          </span>
        </div>
      )}
    </div>
  )
}
